import { useEffect, useState } from "react";
import { format } from "date-fns";
import { CreditCard, ScrollText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, money } from "@/lib/api";
import { Loader, EmptyState, PageHeader } from "@/components/common/Primitives";
import { StatusBadge } from "@/components/common/StatusBadge";

export function AdminPayments() {
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("");
  useEffect(() => { setItems(null); api.get("/admin/payments", { params: { status } }).then((r) => setItems(r.data.items)); }, [status]);
  return (
    <div>
      <PageHeader title="Payments" subtitle="Only payments verified by Razorpay signature or webhook are marked paid." />
      <div className="mb-5 flex gap-1.5">
        {["", "paid", "created", "failed"].map((s) => (
          <button key={s || "all"} onClick={() => setStatus(s)} data-testid={`payments-filter-${s || "all"}`}
            className={`rounded-full px-3 py-1.5 text-xs font-medium capitalize ${status === s ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>{s || "All"}</button>
        ))}
      </div>
      {!items ? <Loader /> : items.length === 0 ? <EmptyState icon={CreditCard} title="No payments" testId="payments-empty" /> : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="admin-payments-table">
            <thead><tr className="text-left text-xs uppercase text-slate-400"><th className="p-3">Date</th><th className="p-3">Restaurant / Owner</th><th className="p-3">Plan</th><th className="p-3">Amount</th><th className="p-3">Mode</th><th className="p-3">Status</th><th className="p-3">Razorpay IDs</th></tr></thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="p-3">{format(new Date(p.created_at), "dd MMM yyyy")}</td>
                  <td className="p-3" data-testid={`payment-restaurant-${p.id}`}><p className="font-medium text-slate-800">{p.restaurant_name || "—"}</p><p className="text-xs text-slate-400">{p.owner_name}</p></td>
                  <td className="p-3">{p.plan_name}</td>
                  <td className="p-3">{money(p.amount)}</td><td className="p-3 capitalize">{p.mode}</td>
                  <td className="p-3"><StatusBadge status={p.status} /></td>
                  <td className="p-3 font-mono text-xs text-slate-500">{p.razorpay_order_id}<br />{p.razorpay_payment_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function AdminAudit() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  useEffect(() => { api.get("/admin/audit-logs", { params: { page } }).then((r) => setData(r.data)); }, [page]);
  if (!data) return <Loader />;
  return (
    <div>
      <PageHeader title="Audit log" subtitle="Sensitive admin and billing actions." />
      {data.items.length === 0 ? <EmptyState icon={ScrollText} title="No activity yet" /> : (
        <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white" data-testid="audit-list">
          {data.items.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm">
              <div><p className="font-medium text-slate-800">{a.action.replaceAll("_", " ")}</p><p className="text-xs text-slate-400">{a.actor_email} · {a.details}</p></div>
              <span className="text-xs text-slate-400">{format(new Date(a.created_at), "dd MMM yyyy, HH:mm")}</span>
            </div>
          ))}
        </div>
      )}
      <div className="mt-4 flex items-center gap-3">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="audit-prev">Prev</Button>
        <span className="text-sm text-slate-500">Page {data.page} of {Math.max(data.pages, 1)}</span>
        <Button size="sm" variant="outline" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="audit-next">Next</Button>
      </div>
    </div>
  );
}
